import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Product {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  listPrice: number;
  currency: string;
  status: string;
}

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}

export function ProductsPage() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get<Product[]>('/products')
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function handleArchive(id: string) {
    apiClient
      .patch(`/products/${id}`, { status: 'ARCHIVED' })
      .then(() => load())
      .catch(() => {});
  }

  return (
    <div className="space-y-4">
      <CreateProductModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={load}
      />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('nav.products')}</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          + Product
        </Button>
      </div>
      {loading && (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}
      <ul className="divide-y rounded-lg border bg-card">
        {products.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between px-4 py-3"
          >
            <div>
              <div className="font-medium">{p.name}</div>
              <div className="text-xs text-muted-foreground">
                {p.sku} &middot; {formatPrice(p.listPrice, p.currency)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {p.status === 'ACTIVE' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleArchive(p.id)}
                >
                  Archive
                </Button>
              )}
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  p.status === 'ACTIVE'
                    ? 'bg-emerald-900/40 text-emerald-200'
                    : 'bg-muted text-foreground'
                }`}
              >
                {p.status}
              </span>
            </div>
          </li>
        ))}
        {!loading && products.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">
            No products found
          </li>
        )}
      </ul>
    </div>
  );
}

function CreateProductModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [listPrice, setListPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!sku.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/products', {
        sku: sku.trim(),
        name: name.trim(),
        description: description.trim() || undefined,
        listPrice: parseFloat(listPrice) || 0,
        currency,
      });
      setSku('');
      setName('');
      setDescription('');
      setListPrice('');
      setCurrency('USD');
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to create product';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">Create Product</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              SKU <span className="text-red-500">*</span>
            </label>
            <Input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="e.g. PROD-001"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Product name"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">List Price</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={listPrice}
                onChange={(e) => setListPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Currency</label>
              <Input
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                placeholder="USD"
              />
            </div>
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={submitting || !sku.trim() || !name.trim()}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
